import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, from, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  finalize,
  groupBy,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ApiContributorItem, CatalogItem } from '../models/catalog/catalog-item.model';
import { CatalogResponse } from '../models/catalog/catalog-response.model';
import { CatalogService } from '../services/catalog/catalog.service';
import {
  cloudScriptFunctionName,
  CloudScriptService,
  purchaseItemRequestBody
} from '../services/cloud-script/cloud-script.service';
import { LocalStorageService } from '../services/local-storage/local-storage.service';
import { extractIdsFromErrorMessage } from '../services/utils/utils.service';
import * as CatalogActions from './catalog.actions';
import {
  deleteInventoryItems,
  getAllDraftItems,
  getContributorItemsNoCache,
  getCreatorStoreAdventureItems,
  getCreatorStoreUniverseItems,
  getDraftItem,
  getDraftItemsNoCache,
  getPlayerStoreItems,
  getRecycledDraftItems,
  getRecycledInventoryItems,
  setModerationState,
  updateInventoryItem
} from './catalog.actions';
import { CatalogState } from './catalog.reducer';
import { ProfilesState } from './profiles/profiles.reducer';



@Injectable()
export class CatalogEffects {
  private localStorageService = inject(LocalStorageService);
  private actions$ = inject(Actions);
  private cloudScriptService = inject(CloudScriptService);
  private catalogService = inject(CatalogService);
  private store = inject<Store<{
    catalog: CatalogState;
    profiles: ProfilesState;
}>>(Store);

  getItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType('get-draft-items'),
      withLatestFrom(this.store.select((state) => state)),
      filter(
        ([_, state]) => !state.catalog.data || state.catalog.data.length === 0,
      ),
      tap(() => this.store.dispatch({ type: '[Catalog] Loading Get Items' })),
      exhaustMap(([id, items]) =>
        from(this.catalogService.getEntityDraftItems()).pipe(
          map((catalogResponse: CatalogResponse) => {
            const differentOfIsHidden = catalogResponse?.data?.Items.filter(
              (item: any) => {
                if (!item.DisplayProperties.Recycled) return item;
              },
            );
            return {
              type: '[Catalog] Success Get Draft Items',
              payload: differentOfIsHidden,
            };
          }),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Get Draft Items',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({
              type: '[Catalog] Loaded Get Draft Items',
              payload: { loaded: true },
            }),
          ),
        ),
      ),
    ),
  );

  //This one recursively gets all draft items
  getAllItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAllDraftItems),
      withLatestFrom(this.store.select((state: any) => state.catalog)),
      filter(([_, state]) => !state.data || state.data.length === 0),
      tap(() => this.store.dispatch({ type: '[Catalog] Loading Get Items' })),
      exhaustMap(([id, items]) =>
        from(this.catalogService.getAllEntityDraftItems()).pipe(
          map((catalogResponse: CatalogResponse) => {
            const differentOfIsRecycled = catalogResponse?.data?.Items.filter(
              (item: any) => {
                if (!item.DisplayProperties.Recycled) return item;
              },
            );
            return {
              type: '[Catalog] Success Get Draft Items',
              payload: differentOfIsRecycled,
            };
          }),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Get Draft Items',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({
              type: '[Catalog] Loaded Get Draft Items',
              payload: { loaded: true },
            }),
          ),
        ),
      ),
    ),
  );

  //This is the same as getItems$ but it doesn't use the cache, and it change the loading property so it can happen in the background
  getItemsNoCache$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDraftItemsNoCache),
      exhaustMap(() =>
        this.catalogService.getEntityDraftItems().pipe(
          map((catalogResponse: CatalogResponse) => {
            const differentOfIsRecycled = catalogResponse?.data?.Items.filter(
              (item: any) => {
                if (!item.DisplayProperties.Recycled) return item;
              },
            );
            return {
              type: '[Catalog] Success Get Items',
              payload: differentOfIsRecycled,
            };
          }),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Get Items',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({ type: '[Catalog] Loaded Get Items' }),
          ),
        ),
      ),
    ),
  );

  getItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDraftItem),
      groupBy((action) => action.id), // Group actions by payload id
      mergeMap((group) =>
        group.pipe(
          withLatestFrom(this.store.select((state) => state)),
          exhaustMap(([payload, state]) => {
            // Use exhaustMap to ignore new actions while a request is ongoing
            const item = state.catalog.allItems.find(
              (catalogItem) => payload.id === catalogItem.item.Id,
            );
            this.store.dispatch({ type: '[Catalog] Loading Get Draft Item' });
            return from(this.catalogService.getDraftItem(payload.id)).pipe(
              map((catalogResponse) => {
                if (item) {
                  return {
                    type: '[Catalog] Success Get Draft Item',
                    payload: catalogResponse.data.Item,
                  };
                } else {
                  return { type: '', payload: catalogResponse.data.Item };
                }
              }),
              catchError((error) => {
                return of({
                  type: '[Catalog] Error Get Draft Item',
                  error: error?.error?.errorMessage,
                });
              }),
              finalize(() =>
                this.store.dispatch({
                  type: '[Catalog] Loaded Get Draft Item',
                  payload: { loaded: true },
                }),
              ),
            );
          }),
        ),
      ),
    ),
  );

  getPublicItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.getPublicItem),
      mergeMap((action) => // Use mergeMap to allow multiple requests if needed
        this.catalogService.getItem(action.id).pipe(
          map(response => {
            if (response.code === '200' && response.data.Item) {
              return CatalogActions.getPublicItemSuccess({ item: response.data.Item });
            } else {
              return CatalogActions.getPublicItemFailure({ error: response });
            }
          }),
          catchError(error => of(CatalogActions.getPublicItemFailure({ error })))
        )
      )
    )
  );

  getRecycledDraftItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRecycledDraftItems),
      withLatestFrom(
        this.store.select((state: any) => state.catalog.archivedDraftItems),
      ),
      filter(([_, state]) => !state?.data),
      tap(() =>
        this.store.dispatch({
          type: '[Catalog] Loading Get Recycled Draft Items',
        }),
      ),
      exhaustMap(([id, items]) =>
        from(this.catalogService.getAllEntityDraftItems()).pipe(
          map((catalogResponse: CatalogResponse) => {
            const hiddenItems = catalogResponse?.data?.Items.filter(
              (item: any) => {
                if (item.DisplayProperties.Recycled) return item;
              },
            );
            return {
              type: '[Catalog] Success Get Recycled Draft Items',
              payload: hiddenItems,
            };
          }),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Get Recycled Draft Items',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({
              type: '[Catalog] Loaded Get Recycled Draft Items',
            }),
          ),
        ),
      ),
    ),
  );

  getRecycledInventoryItems$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(getRecycledInventoryItems),
        withLatestFrom(
          this.store.select((state) => state.catalog.recycledInventoryItems),
        ),
        filter(([_, state]) => !state?.data),
        tap(() =>
          this.store.dispatch({
            type: '[Catalog] Loading Get Recycled Inventory Items',
          }),
        ),
        exhaustMap(([id, items]) =>
          this.catalogService.GetInventoryItems().pipe(
            map((catalogResponse) => {
              // To filter Just the Recycled equals false
              const itemsIdsFiltered = catalogResponse.data.Items.filter(
                (item) => {
                  item?.DisplayProperties?.['Recycled'];
                },
              ).map((item) => {
                return item.Id;
              });
              return { type: 'Sending ids', payload: itemsIdsFiltered };
            }),
            concatMap((response2) => {
              const itemsIds = response2.payload;

              if (itemsIds.length > 0) {
                return getItemsWithRetry(
                  itemsIds,
                  this.catalogService,
                  this.store,
                  4,
                );
              }
            }),
            tap((response3: any) => {
              return this.store.dispatch({
                type: '[Catalog] Success Get Recycled Inventory Items',
                payload: response3.data.Items,
              });
            }),
            catchError((error) => {
              return of({
                type: '[Catalog] Error Get Recycled Inventory Items',
                error: error?.error?.errorMessage,
              });
            }),
            finalize(() =>
              this.store.dispatch({
                type: '[Catalog] Loaded Get Recycled Inventory Items',
              }),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  deleteItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType('delete-draft-item'),
      withLatestFrom(this.store.select((state) => state.catalog)),
      tap((data: ActionAndCatalogState) => {
        return this.store.dispatch({
          type: '[Catalog] Loading Delete Item',
          payload: {
            id: data[0].payload.id,
          },
        });
      }),
      exhaustMap((data) => {
        const id = data[0].payload.id;
        const item = data[1].allItems.find((item) => item.item.Id === id);

        return this.catalogService
          .updateDraftItem({
            Item: {
              ...item.item,
              PriceOptions: null,
              DisplayProperties: {
                ...item.item.DisplayProperties,
                Recycled: true,
              },
            },
          })
          .pipe(
            map(() => ({
              type: '[Catalog] Success Delete Item',
              payload: {
                id,
              },
            })),
            catchError((error) => {
              return of({
                type: '[Catalog] Error Delete Item',
                error: error?.error?.errorMessage,
              });
            }),
            finalize(() =>
              this.store.dispatch({ type: '[Catalog] Loaded Delete Item' }),
            ),
          );
      }),
    ),
  );

  createItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType('create-draft-item'),
      withLatestFrom(this.store.select((state: any) => state.catalog)),
      tap((data: any) =>
        this.store.dispatch({
          type: '[Catalog] Loading Create Item',
          payload: {
            item: data[0].payload.item.Item,
          },
        }),
      ),
      exhaustMap((data: any) => {
        const item = data[0].payload.item;
        return this.catalogService.saveCatalogDraftItem(item).pipe(
          map(
            (response: {
              code: number;
              data: { Item: CatalogItem };
              status: string;
            }) => {
              return {
                type: '[Catalog] Success Create Item',
                payload: {
                  item: response.data.Item,
                },
              };
            },
          ),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Create Item',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({ type: '[Catalog] Loaded Create Item' }),
          ),
        );
      }),
    ),
  );

  updateItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType('update-draft-item'),
      tap((data: any) => {
        // console.log('🎯 NgRx effect update-draft-item recibido:', data);
        return this.store.dispatch({
          type: '[Catalog] Loading Update Item',
          payload: {
            item: data.payload.item,
          },
        });
      }),
      exhaustMap(
        (data: {
          payload: {
            item: CatalogItem;
            publish?: boolean;
          };
        }) => {
          const item = data.payload.item;
          const publish = data.payload.publish;

          // console.log('🔄 Ejecutando effect con item:', item.Id, 'publish:', publish);

          return this.cloudScriptService.getDraftItem(item.Id).pipe(
            switchMap((response) => {
              // console.log('📦 Effect getDraftItem response:', response);
              const newItem = {
                ...item,
                Contents: response.data.FunctionResult.Item.Contents,
              };
              /* console.log('🔧 Effect execute UpdateDraftItem:', {
                FunctionName: cloudScriptFunctionName.UpdateDraftItem,
                Item: newItem,
                Publish: publish
              }); */
              return this.cloudScriptService.executeFunction({
                FunctionName: cloudScriptFunctionName.UpdateDraftItem,
                FunctionParameter: {
                  Item: newItem,
                  Publish: publish,
                },
              });
            }),
            map((response) => {
              console.log('✅ Effect UpdateDraftItem success:', response);
              return {
                type: '[Catalog] Success Update Item',
                payload: {
                  item: response.data.FunctionResult.Item,
                },
              };
            }),
            catchError((error) => {
              console.error('❌ Effect UpdateDraftItem error:', error);
              return of({
                type: '[Catalog] Error Update Item',
                error: error?.error?.errorMessage,
              });
            }),
            finalize(() =>
              this.store.dispatch({ type: '[Catalog] Loaded Update Item' }),
            ),
          );
        },
      ),
    ),
  );

  clearStore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType('clear-store'),
        tap(() => {
          this.store.dispatch({ type: '[Catalog] Clear store' });
        }),
      ),
    { dispatch: false },
  );

  // getInventory$ = createEffect(() => this.actions$.pipe(
  //     ofType('get-inventory-items'),
  //     withLatestFrom(this.store.select((state: any) => state.catalog)),
  //     filter(([_, state]) => !state.inventoryItems || state.inventoryItems.length === 0),
  //     tap(() => this.store.dispatch({ type: '[Catalog] Loading Get Inventory Items' })),
  //     concatMap(([id, items]) => this.catalogService.GetInventoryItems()
  //         .pipe(
  //             map(response => response.data.Items.map((item: any) => item.Id)),
  //             concatMap(itemsIds => {
  //                 return this.catalogService.getItems(itemsIds)
  //             }),
  //             tap(response2 => this.store.dispatch({ type: '[Catalog] Success Get Inventory Items', payload: response2 })),
  //             catchError(error => {

  //                 this.store.dispatch({ type: '[Catalog] Error Get Inventory Items', error: error?.error?.errorMessage });
  //                 return of({ type: '[Catalog] Error Get Inventory Items', error: error?.error?.errorMessage });
  //             }),
  //             finalize(() => this.store.dispatch({ type: '[Catalog] Loaded Get Inventory Items' }))
  //         )
  //     ),
  // ), { dispatch: false });

  getInventory$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType('get-inventory-items'),
        withLatestFrom(this.store.select((state) => state.catalog)),
        filter(
          ([_, state]) =>
            !state.inventoryItems || state.inventoryItems.length === 0,
        ),
        tap(() =>
          this.store.dispatch({
            type: '[Catalog] Loading Get Inventory Items',
          }),
        ),
        concatMap(([id, items]) =>
          this.catalogService.GetInventoryItems().pipe(
            map((response) => {
              // To filter Just the Recycled equals false
              const inventoryItems = response.data.Items.filter((item) => {
                return !item?.DisplayProperties?.['Recycled'];
              });

              return inventoryItems;
            }),
            concatMap((inventoryItems) => {
              if (inventoryItems.length > 0)
                return getItemsWithRetry(
                  inventoryItems.map((item) => item.Id),
                  this.catalogService,
                  this.store,
                  4,
                ).pipe(
                  map((response2) => ({
                    response2,
                    inventoryItems,
                  })),
                );
            }),
            tap(({ response2, inventoryItems }) => {
              this.store.dispatch({
                type: '[Catalog] Success Get Inventory Items',
                payload: {
                  response: response2,
                  inventoryItems: inventoryItems,
                },
              });
            }),
            finalize(() =>
              this.store.dispatch({
                type: '[Catalog] Loaded Get Inventory Items',
              }),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  deleteInventoryItems$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteInventoryItems),
        exhaustMap((data) =>
          this.catalogService.DeleteInventoryItems(data).pipe(
            map((response) =>
              this.store.dispatch({
                type: '[Catalog] Success Delete Inventory Items',
                payload: data,
              }),
            ),
            catchError((error) => {
              // Handle the error here
              this.store.dispatch({
                type: '[Catalog] Error Delete Inventory Items',
                payload: error.error.errorMessage,
              });
              // Return an empty observable to prevent the stream from collapsing
              return EMPTY;
            }),
            finalize(() =>
              this.store.dispatch({
                type: '[Catalog] Loaded Delete Inventory Items',
              }),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  /**
   * this effect orchestrates the process of purchasing inventory items,
   * handling success and error cases, and dispatching corresponding actions to update the application state.
   */
  purchaseInventoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType('purchase-item'),
      withLatestFrom(this.store.select((state: any) => state.catalog)),

      tap((data: any) =>
        this.store.dispatch({
          type: '[Catalog] Loading Create Inventory Item',
          payload: data[0].payload,
        }),
      ),

      exhaustMap((data: any) => {
        const purchaseItemRequestBody: purchaseItemRequestBody = data[0];
        return this.cloudScriptService
          .purchaseItemAndParent(purchaseItemRequestBody) //calls a cloud script service
          .pipe(
            map((response) => {
              return {
                type: '[Catalog] Success Create Inventory Item',
                payload: response.data.FunctionResult.Items,
              };
            }),
            catchError((error) => {
              return of({
                type: '[Catalog] Error Create Inventory Item',
                error: error?.error?.errorMessage,
              });
            }),

            finalize(() =>
              this.store.dispatch({
                type: '[Catalog] Loaded Create Inventory Item',
              }),
            ),
          );
      }),
    ),
  );

  updateInventoryItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateInventoryItem),
      withLatestFrom(this.store.select((state: any) => state.catalog)),
      filter(([_, state]) => !state?.data),
      tap((data: any) => {
        return this.store.dispatch({
          type: '[Catalog] Loading Update Inventory Item',
          payload: {
            item: data.payload.item,
          },
        });
      }),
      exhaustMap((data: any) => {
        const item = data.payload.item;
        return this.catalogService.updateInventoryItem(item).pipe(
          map(
            (response: {
              code: number;
              data: { Item: CatalogItem };
              status: string;
            }) => {
              return {
                type: '[Catalog] Success Update Inventory Item',
                payload: {
                  item: response.data.Item,
                },
              };
            },
          ),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Update Inventory Item',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({
              type: '[Catalog] Loaded Update Inventory Item',
            }),
          ),
        );
      }),
    ),
  );

  /*   getCrmItems$ = createEffect(() =>
      this.actions$.pipe(
        ofType(getCrmItems),
        withLatestFrom(this.store.select((state: any) => state.catalog.crm)),
        filter(
          ([_, state]) =>
            state.data.Items.AwaitingModeration.Total == 0 &&
            state.data.Items.Approved.Total == 0 &&
            state.data.Items.Rejected.Total == 0,
        ),
        tap(() =>
          this.store.dispatch({ type: '[Catalog] Loading Get Crm Items' }),
        ),
        exhaustMap(() =>
          this.cloudScriptService
            .executeFunction({
              FunctionName: cloudScriptFunctionName.GetItemsWithModeration,
            })
            .pipe(
              map((response) => {
                return {
                  type: '[Catalog] Success Get Crm Items',
                  payload: response.data.FunctionResult.data,
                };
              }),
              catchError((error) => {
                return of({
                  type: '[Catalog] Error Get Crm Items',
                  error: error?.error?.errorMessage,
                });
              }),
              finalize(() =>
                this.store.dispatch({ type: '[Catalog] Loaded Get Crm Items' }),
              ),
            ),
        ),
      ),
    ); */

  /*   getNextCrmItems$ = createEffect(() =>
      this.actions$.pipe(
        ofType('get-more-crm-items'),
        tap(() =>
          this.store.dispatch({ type: '[Catalog] Loading Get Next Crm Items' }),
        ),
        withLatestFrom(this.store),
        exhaustMap(([action, state]: any) => {
          const continuationToken = state.catalog.crm.data.ContinuationToken;
  
          return iif(
            () => continuationToken !== null,
            this.cloudScriptService
              .executeFunction({
                FunctionName: cloudScriptFunctionName.GetItemsWithModeration,
                FunctionParameter: {
                  ContinuationToken: continuationToken,
                },
              })
              .pipe(
                map((response) => {
                  return {
                    type: '[Catalog] Success Get Next Crm Items',
                    payload: response.data.FunctionResult.data,
                  };
                }),
                catchError((error) => {
                  return of({
                    type: '[Catalog] Error Get Next Crm Items',
                    error: error?.error?.errorMessage,
                  });
                }),
                finalize(() =>
                  this.store.dispatch({
                    type: '[Catalog] Loaded Get Next Crm Items',
                  }),
                ),
              ),
            throwError(() => new Error('Continuation token is null')), // This will interrupt the effect and throw an error
          );
        }),
      ),
    ); */

  getCreatorStoreUniverseItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCreatorStoreUniverseItems),
      withLatestFrom(
        this.store.select((state) => state.catalog.creatorStoreUniverses),
      ),
      filter(([_, state]) => !state?.data),
      tap(() =>
        this.store.dispatch({
          type: '[Catalog] Loading Get Creator Store Universe Items',
        }),
      ),
      exhaustMap(() =>
        this.catalogService.searchCreatorStoreList('Universe').pipe(
          switchMap((response: CatalogResponse) => {
            // This add the Images to every searched item
            const itemObservables = response.data.Items.map((item: any) => {
              // API call to get item details, including the thumbnail image URL
              return this.catalogService.getItem(item.Id).pipe(
                map((itemDetailsResponse: any) => {
                  return {
                    ...item,
                    Images: itemDetailsResponse.data?.Item?.Images,
                  };
                }),
              );
            });

            // ForkJoin to combine multiple observables into one
            return forkJoin(itemObservables).pipe(
              map((itemsWithImages: []) => {
                // To get store items different from user
                // const itemsDifferentFromUser = itemsWithImages.filter((r) => {
                //     const titlePlayerAccount: any = this.localStorageService.get("titlePlayerAccount")
                //     if (r.CreatorEntity?.Id !== titlePlayerAccount) return r
                // })

                // Now, itemsWithImages contains items with thumbnailImage property
                return {
                  type: '[Catalog] Success Get Creator Store Universe Items',
                  payload: itemsWithImages,
                };
              }),
            );
          }),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Get Creator Store Universe Items',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({
              type: '[Catalog] Loaded Get Creator Store Universe Items',
            }),
          ),
        ),
      ),
    ),
  );

  getCreatorStoreAdventureItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCreatorStoreAdventureItems),
      withLatestFrom(
        this.store.select((state) => state.catalog.creatorStoreAdventures),
      ),
      filter(([_, state]) => !state?.data),
      tap(() =>
        this.store.dispatch({
          type: '[Catalog] Loading Get Creator Store Adventure Items',
        }),
      ),
      exhaustMap(() =>
        this.catalogService.searchCreatorStoreList('Adventure').pipe(
          switchMap((response: CatalogResponse) => {
            // This add the Images to every searched item
            const itemObservables = response.data.Items.map((item: any) => {
              // API call to get item details, including the thumbnail image URL
              return this.catalogService.getItem(item.Id).pipe(
                map((itemDetailsResponse: any) => {
                  return {
                    ...item,
                    Images: itemDetailsResponse.data?.Item?.Images,
                  };
                }),
              );
            });

            // ForkJoin to combine multiple observables into one
            return forkJoin(itemObservables).pipe(
              map((itemsWithImages: []) => {
                // Now, itemsWithImages contains items with thumbnailImage property
                return {
                  type: '[Catalog] Success Get Creator Store Adventure Items',
                  payload: itemsWithImages,
                };
              }),
            );
          }),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Get Creator Store Adventure Items',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({
              type: '[Catalog] Loaded Get Creator Store Adventure Items',
            }),
          ),
        ),
      ),
    ),
  );

  getPlayerStoreItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPlayerStoreItems),
      withLatestFrom(
        this.store.select((state: any) => state.catalog.creatorStoreUniverses),
      ),
      filter(([_, state]) => !state?.data),
      tap(() =>
        this.store.dispatch({
          type: '[Catalog] Loading Get Player Store Items',
        }),
      ),
      exhaustMap(() =>
        this.catalogService.searchPlayerStoreList().pipe(
          switchMap((response: CatalogResponse) => {
            // This add the Images to every searched item
            const itemObservables = response.data.Items.map((item: any) => {
              // API call to get item details, including the thumbnail image URL
              return this.catalogService.getItem(item.Id).pipe(
                map((itemDetailsResponse: any) => {
                  return {
                    ...item,
                    Images: itemDetailsResponse.data?.Item?.Images,
                  };
                }),
              );
            });

            // ForkJoin to combine multiple observables into one
            return forkJoin(itemObservables).pipe(
              map((itemsWithImages: any[]) => {
                // To get store items different from user
                const itemsDifferentFromUser = itemsWithImages.filter((r) => {
                  const titlePlayerAccount: any =
                    this.localStorageService.get('titlePlayerAccount');
                  if (r.CreatorEntity?.Id !== titlePlayerAccount) return r;
                });
                // Now, itemsWithImages contains items with thumbnailImage property
                return {
                  type: '[Catalog] Success Get Player Store Items',
                  payload: itemsDifferentFromUser,
                };
              }),
            );
          }),
          catchError((error) => {
            return of({
              type: '[Catalog] Error Get Player Store Items',
              error: error?.error?.errorMessage,
            });
          }),
          finalize(() =>
            this.store.dispatch({
              type: '[Catalog] Loaded Get Player Store Items',
            }),
          ),
        ),
      ),
    ),
  );

  setModerationCrmItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setModerationState),
      withLatestFrom(this.store.select((state: any) => state.catalog.crm)),
      tap((data) => {
        const now = new Date();
        return this.store.dispatch({
          type: '[Catalog] Loading Set Moderation Crm Item',
          payload: {
            ...data[0],
            LastModifiedDate: now.toISOString(),
          },
        });
      }),
      exhaustMap((payload) => {
        return this.cloudScriptService
          .setItemModerationState({
            Id: payload[0].Id,
            Status: payload[0].Status,
            Reason: payload[0]?.Reason,
          })
          .pipe(
            map((response) => {
              return { type: '[Catalog] Success Set Moderation Crm Item' };
            }),
            catchError((error) => {
              return of({
                type: '[Catalog] Error Set Moderation Crm Item',
                error: error?.error?.errorMessage,
              });
            }),
            finalize(() =>
              this.store.dispatch({
                type: '[Catalog] Loaded Set Moderation Crm Item',
              }),
            ),
          );
      }),
    ),
  );
  getContributorItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CatalogActions.getContributorItems),
      withLatestFrom(this.store.select((state) => state.catalog.contributors)),
      tap(([action, contributorsState]) => {
        this.store.dispatch({ type: '[Catalog] Loading Get Contributor Items' });
      }),
      exhaustMap(() =>
        this.catalogService.getContributorDraftAdventures().pipe(
          map((response) => {
            if (response.success && Array.isArray(response.data)) {
              const catalogItems: CatalogItem[] = response.data.map(
                (apiItem: ApiContributorItem) => { // apiItem is an object from your API's data array
                  // Explicitly map the API response to your internal CatalogItem model
                  const mappedItem: CatalogItem = {
                    // Map properties with different casing or structure
                    Id: apiItem.id,
                    Title: { NEUTRAL: apiItem.title?.neutral || 'Untitled' },
                    Description: { NEUTRAL: apiItem.description?.neutral || '' },
                    CreatorEntity: apiItem.creatorEntity ? {
                      Id: apiItem.creatorEntity.id,
                      Type: apiItem.creatorEntity.type,
                      TypeString: apiItem.creatorEntity.typeString,
                    } : undefined,
                    DisplayProperties: apiItem.displayProperties ? {
                      ParentUniverseID: apiItem.displayProperties['parentUniverseId'],
                      Recycled: apiItem.displayProperties['recycled'],
                      Shared: apiItem.displayProperties['shared'],
                      OriginalItemId: apiItem.displayProperties['originalItemId'],
                      ParentItemId: apiItem.displayProperties['parentItemId'],
                    } : {},

                    Images: Array.isArray(apiItem.images) ? apiItem.images.map((imgFromApi: any) => ({
                      Id: imgFromApi.id,
                      Tag: imgFromApi.tag,
                      Type: imgFromApi.type,
                      Url: imgFromApi.url,
                    })) : [],

                    // Directly assign properties where casing and structure already match
                    Type: apiItem.type,
                    AlternateIds: apiItem.alternateIds,
                    Keywords: apiItem.keywords,
                    ContentType: apiItem.contentType,
                    Platforms: apiItem.platforms,
                    Tags: apiItem.tags,
                    CreationDate: apiItem.creationDate,
                    LastModifiedDate: apiItem.lastModifiedDate,
                    StartDate: apiItem.startDate,
                    Contents: apiItem.contents,
                    ItemReferences: apiItem.itemReferences,
                    DeepLinks: apiItem.deepLinks,
                    ETag: apiItem.etag,
                    PriceOptions: apiItem.priceOptions,
                    Moderation: apiItem.moderation,
                    IsHidden: apiItem.isHidden
                  };
                  return mappedItem;
                }
              );
              return {
                type: '[Catalog] Success Get Contributor Items',
                payload: catalogItems,
              };
            } else {
              return {
                type: '[Catalog] Error Get Contributor Items',
                error: response.error || 'API returned success:false or data not an array',
              };
            }
          }),
          catchError((error) => {
            console.error('[Effect] HTTP/Service Error in getContributorItems$:', error);
            return of({
              type: '[Catalog] Error Get Contributor Items',
              error: error?.message || error?.error?.errorMessage || 'Unknown HTTP error in effect',
            });
          }),
          finalize(() => {
          })
        ),
      ),
    ),
  );

  getContributorItemsNoCache$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getContributorItemsNoCache),
      tap(() =>
        this.store.dispatch({
          type: '[Catalog] Loading Get contributor Items',
        }),
      ),
      exhaustMap(() =>
        this.catalogService.getContributorDraftAdventures().pipe(
          map((response) => {
            if (response.success && response.data) {
              return {
                type: '[Catalog] Success Get contributor Items from Adventure forge API',
                payload: response.data,
              };
            } else {
              return {
                type: '[Catalog] Error Get contributor Items',
                error: response.error || 'Failed to fetch contributor items from Adventure forge API',
              };
            }
          }),
          catchError((error) => {
            console.error('[Effect] HTTP/Service Error in getContributorItemsNoCache$:', error);
            return of({
              type: '[Catalog] Error Get contributor Items',
              error: error?.message || error?.error?.errorMessage || 'Failed to fetch contributor items',
            });
          }),
          finalize(() =>
            this.store.dispatch({
              type: '[Catalog] Loaded Get contributor Items',
            }),
          ),
        ),
      ),
    ),
  );
}

/**
 * Recursive function to get the inventory items by existing Id's
 * @param itemsIds
 * @param catalogService
 * @param store
 * @param retryCount
 * @returns
 */
// function getItemsWithRetry(itemsIds: any[], catalogService, store, retryCount: number) {
//     if (retryCount <= 0) {
//         // handle the case when retry limit is reached

//         store.dispatch({ type: '[Catalog] Error Get Inventory Items', error: 'Retry limit reached' });
//         return of({ type: '[Catalog] Error Get Inventory Items', error: 'Retry limit reached' });
//     }
//     return catalogService.getItems(itemsIds).pipe(
//         catchError(error => {
//             const errorIds = extractIdsFromErrorMessage(error?.error?.errorMessage);

//             if (errorIds.length > 0) {
//                 const newItemIds = itemsIds.filter(item => !errorIds.includes(item));
//                 return getItemsWithRetry(newItemIds, catalogService, store, retryCount - 1);
//             }
//             // store.dispatch({ type: '[Catalog] Error Get Inventory Items', error: error?.error?.errorMessage });
//             // return of({ type: '[Catalog] Error Get Inventory Items', error: error?.error?.errorMessage });
//         })
//     );
// }

/**
 * Recursive function to get the inventory items by existing Id's
 * @param itemsIds
 * @param catalogService
 * @param store
 * @param retryCount
 * @returns
 */
function getItemsWithRetry(
  itemsIds: any[],
  catalogService,
  store,
  retryCount: number,
) {
  if (retryCount <= 0) {
    // handle the case when retry limit is reached
    store.dispatch({
      type: '[Catalog] Error Get Inventory Items',
      error: 'Retry limit reached',
    });
    return of({
      type: '[Catalog] Error Get Inventory Items',
      error: 'Retry limit reached',
    });
  }
  return catalogService.getItems(itemsIds).pipe(
    catchError((error) => {
      const errorIds = extractIdsFromErrorMessage(error?.error?.errorMessage);
      if (errorIds.length > 0) {
        const newItemIds = itemsIds.filter((item) => !errorIds.includes(item));
        return getItemsWithRetry(
          newItemIds,
          catalogService,
          store,
          retryCount - 1,
        );
      }
      store.dispatch({
        type: '[Catalog] Error Get Inventory Items',
        error: error?.error?.errorMessage,
      });
      return of({
        type: '[Catalog] Error Get Inventory Items',
        error: error?.error?.errorMessage,
      });
    }),
  );
}

type ActionAndCatalogState = [
  {
    type: string;
    payload: {
      id: string;
    };
  },
  CatalogState,
];
